/**
 * Utilidades para generar PDFs en formato térmico (80mm o 58mm)
 * Compatible con impresoras de tickets
 */
import jsPDF from 'jspdf';

// Dimensiones del papel térmico en mm - AJUSTADAS para mejor legibilidad
const PAPER_SIZES = {
  80: { width: 72, marginX: 2 },  // 80mm real = ~72mm imprimible
  58: { width: 48, marginX: 2 }   // 58mm real = ~48mm imprimible
};

/**
 * Formatea un valor como moneda (formato corto)
 */
const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return '$' + num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Deduce el formato de imagen que espera jsPDF (PNG/JPEG/WEBP) a partir
 * del mime type de un data URI (data:image/...;base64,...).
 */
const getImageFormat = (dataUrl) => {
  const match = /^data:image\/(\w+);base64,/i.exec(dataUrl || '');
  const type = (match?.[1] || 'png').toLowerCase();
  if (type === 'jpg' || type === 'jpeg') return 'JPEG';
  if (type === 'webp') return 'WEBP';
  return 'PNG';
};

/**
 * Dibuja el logo de la empresa centrado, si está configurado y habilitado.
 * Devuelve la nueva posición Y (después del logo, con su espaciado).
 */
const drawThermalLogo = (doc, config, centerX, y, paperWidth) => {
  if (!config?.plantilla_recibo_mostrar_logo || !config?.logo_url) return y;
  if (!config.logo_url.startsWith('data:image')) return y;

  const logoSize = paperWidth === 80 ? 16 : 12;
  try {
    doc.addImage(config.logo_url, getImageFormat(config.logo_url), centerX - logoSize / 2, y, logoSize, logoSize);
    return y + logoSize + 2;
  } catch (error) {
    console.error('Error al agregar logo al ticket:', error);
    return y;
  }
};

/**
 * Formatea una fecha (formato corto)
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

/**
 * Crea un documento PDF con dimensiones para papel térmico
 * @param {number} paperWidth - Ancho del papel (80 o 58)
 * @param {number} estimatedHeight - Altura estimada del contenido en mm
 * @returns {jsPDF} - Documento PDF configurado
 */
export const createThermalDoc = (paperWidth = 80, estimatedHeight = 200) => {
  const size = PAPER_SIZES[paperWidth] || PAPER_SIZES[80];
  
  // Crear documento con dimensiones personalizadas
  // Altura mínima de 100mm, máxima sin límite para tickets largos
  const height = Math.max(estimatedHeight, 100);
  
  return new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [size.width, height]
  });
};

/**
 * Genera un recibo en formato térmico
 * @param {Object} recibo - Datos del recibo
 * @param {Object} config - Configuración de la empresa
 * @param {Array} pagos - Lista de pagos del recibo
 * @param {number} paperWidth - Ancho del papel (80 o 58)
 * @param {boolean} print - Si es true, abre para imprimir; si es false, descarga
 */
export const generateThermalReceipt = (recibo, config, pagos = [], paperWidth = 80, print = false) => {
  const size = PAPER_SIZES[paperWidth] || PAPER_SIZES[80];
  const margin = size.marginX;
  const contentWidth = size.width - (margin * 2);
  const rightEdge = size.width - margin;
  
  // Estimar altura del contenido con más margen
  const items = recibo.items || recibo.conceptos || [];
  const tieneLogo = !!(config?.plantilla_recibo_mostrar_logo && config?.logo_url?.startsWith('data:image'));
  const estimatedHeight = 150 + (items.length * 12) + (pagos.length * 10) + (tieneLogo ? 20 : 0);

  const doc = createThermalDoc(paperWidth, estimatedHeight);
  let y = 4;

  // Configuración de fuentes optimizadas para 80mm
  const fontSize = {
    title: 11,
    subtitle: 9,
    normal: 8,
    small: 7
  };

  const centerX = size.width / 2;

  y = drawThermalLogo(doc, config, centerX, y, paperWidth);

  // === ENCABEZADO DE EMPRESA ===
  const empresa = config?.nombre_empresa || 'Mi Empresa';
  const direccion = config?.direccion || '';
  const telefono = config?.telefono || '';
  
  // Nombre de empresa centrado con tamaño responsivo
  // Ajustar tamaño de fuente según longitud del nombre
  let empresaFontSize = fontSize.title;
  if (empresa.length > 25) {
    empresaFontSize = fontSize.subtitle;
  }
  if (empresa.length > 35) {
    empresaFontSize = fontSize.normal;
  }
  if (empresa.length > 45) {
    empresaFontSize = fontSize.small;
  }
  
  doc.setFontSize(empresaFontSize);
  doc.setFont('helvetica', 'bold');
  const empresaLines = doc.splitTextToSize(empresa, contentWidth);
  empresaLines.forEach(line => {
    doc.text(line, centerX, y, { align: 'center' });
    y += empresaFontSize <= fontSize.small ? 3 : 4;
  });
  
  // Dirección
  if (direccion) {
    doc.setFontSize(fontSize.small);
    doc.setFont('helvetica', 'normal');
    const dirLines = doc.splitTextToSize(direccion, contentWidth);
    dirLines.forEach(line => {
      doc.text(line, centerX, y, { align: 'center' });
      y += 3;
    });
  }
  
  // Teléfono
  if (telefono) {
    doc.setFontSize(fontSize.small);
    doc.text(`Tel: ${telefono}`, centerX, y, { align: 'center' });
    y += 3;
  }
  
  // Línea separadora
  y += 1;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(margin, y, rightEdge, y);
  y += 4;
  
  // === TÍTULO DEL RECIBO ===
  doc.setFontSize(fontSize.subtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('RECIBO', centerX, y, { align: 'center' });
  y += 4;
  
  // Número de recibo
  doc.setFontSize(fontSize.normal);
  doc.setFont('helvetica', 'normal');
  doc.text(`No: ${recibo.numero_recibo || recibo.id}`, centerX, y, { align: 'center' });
  y += 4;
  
  // Línea punteada
  doc.setLineDashPattern([0.5, 0.5], 0);
  doc.line(margin, y, rightEdge, y);
  doc.setLineDashPattern([], 0);
  y += 3;
  
  // === INFORMACIÓN DEL RECIBO ===
  doc.setFontSize(fontSize.normal);
  
  // Fecha y vencimiento en formato compacto
  doc.text(`Fecha: ${formatDate(recibo.fecha_emision)}`, margin, y);
  y += 3;
  doc.text(`Vence: ${formatDate(recibo.fecha_vencimiento)}`, margin, y);
  y += 3;
  
  // Estado
  const estado = (recibo.estado || 'pendiente').toUpperCase();
  doc.setFont('helvetica', 'bold');
  doc.text(`Estado: ${estado}`, margin, y);
  doc.setFont('helvetica', 'normal');
  y += 4;
  
  // Línea punteada
  doc.setLineDashPattern([0.5, 0.5], 0);
  doc.line(margin, y, rightEdge, y);
  doc.setLineDashPattern([], 0);
  y += 3;
  
  // === CLIENTE ===
  doc.setFontSize(fontSize.small);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENTE:', margin, y);
  y += 3;
  
  doc.setFont('helvetica', 'normal');
  const clienteNombre = recibo.cliente_nombre || 'N/A';
  const nombreLines = doc.splitTextToSize(clienteNombre, contentWidth);
  nombreLines.forEach(line => {
    doc.text(line, margin, y);
    y += 3;
  });
  y += 1;
  
  // Línea punteada
  doc.setLineDashPattern([0.5, 0.5], 0);
  doc.line(margin, y, rightEdge, y);
  doc.setLineDashPattern([], 0);
  y += 3;
  
  // === CONCEPTOS ===
  doc.setFontSize(fontSize.small);
  doc.setFont('helvetica', 'bold');
  doc.text('DETALLE', margin, y);
  doc.text('IMPORTE', rightEdge, y, { align: 'right' });
  y += 1;
  doc.setLineWidth(0.2);
  doc.line(margin, y, rightEdge, y);
  y += 3;
  
  doc.setFont('helvetica', 'normal');
  const maxDescWidth = contentWidth - 22; // Espacio para el precio
  
  items.forEach(item => {
    const desc = (item.descripcion || 'Concepto').substring(0, 35);
    const cantidad = item.cantidad || 1;
    const precio = parseFloat(item.precio_unitario) || 0;
    const total = cantidad * precio;
    
    // Descripción truncada si es muy larga
    const descLines = doc.splitTextToSize(desc, maxDescWidth);
    
    doc.text(descLines[0], margin, y);
    doc.text(formatCurrency(total), rightEdge, y, { align: 'right' });
    y += 3;
    
    // Segunda línea de descripción si existe
    if (descLines[1]) {
      doc.text(descLines[1], margin, y);
      y += 3;
    }
    
    // Mostrar cantidad x precio si hay más de 1
    if (cantidad > 1) {
      doc.setFontSize(fontSize.small - 1);
      doc.text(`  ${cantidad} x ${formatCurrency(precio)}`, margin, y);
      doc.setFontSize(fontSize.small);
      y += 3;
    }
  });
  
  y += 1;
  
  // === TOTALES ===
  doc.setLineWidth(0.3);
  doc.line(margin, y, rightEdge, y);
  y += 3;
  
  doc.setFontSize(fontSize.normal);
  
  // Subtotal
  doc.text('Subtotal:', margin, y);
  doc.text(formatCurrency(recibo.subtotal || recibo.monto_total), rightEdge, y, { align: 'right' });
  y += 3;
  
  // IVA si aplica
  if (recibo.monto_iva && parseFloat(recibo.monto_iva) > 0) {
    doc.text(`IVA:`, margin, y);
    doc.text(formatCurrency(recibo.monto_iva), rightEdge, y, { align: 'right' });
    y += 3;
  }
  
  // Descuento si aplica
  if (recibo.monto_descuento && parseFloat(recibo.monto_descuento) > 0) {
    doc.text(`Desc:`, margin, y);
    doc.text(`-${formatCurrency(recibo.monto_descuento)}`, rightEdge, y, { align: 'right' });
    y += 3;
  }
  
  // Línea antes del total
  doc.setLineWidth(0.5);
  doc.line(margin, y, rightEdge, y);
  y += 4;
  
  // TOTAL - Destacado
  doc.setFontSize(fontSize.subtitle + 1);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', margin, y);
  doc.text(formatCurrency(recibo.monto_total), rightEdge, y, { align: 'right' });
  y += 4;
  
  // Saldo pendiente si aplica
  if (parseFloat(recibo.saldo_pendiente) > 0 && recibo.estado !== 'pagado') {
    doc.setFontSize(fontSize.normal);
    doc.text('Saldo:', margin, y);
    doc.text(formatCurrency(recibo.saldo_pendiente), rightEdge, y, { align: 'right' });
    y += 4;
  }
  
  doc.setFont('helvetica', 'normal');
  
  // === HISTORIAL DE PAGOS ===
  if (pagos && pagos.length > 0) {
    y += 1;
    doc.setLineDashPattern([0.5, 0.5], 0);
    doc.line(margin, y, rightEdge, y);
    doc.setLineDashPattern([], 0);
    y += 3;
    
    doc.setFontSize(fontSize.small);
    doc.setFont('helvetica', 'bold');
    doc.text('PAGOS:', margin, y);
    y += 3;
    
    doc.setFont('helvetica', 'normal');
    pagos.slice(0, 3).forEach(pago => {
      doc.text(formatDate(pago.fecha_pago), margin, y);
      doc.text(formatCurrency(pago.monto), rightEdge, y, { align: 'right' });
      y += 3;
    });
  }
  
  // === PIE DE TICKET ===
  y += 3;
  doc.setLineDashPattern([0.5, 0.5], 0);
  doc.line(margin, y, rightEdge, y);
  doc.setLineDashPattern([], 0);
  y += 3;
  
  doc.setFontSize(fontSize.small);
  doc.text('Gracias por su preferencia', centerX, y, { align: 'center' });
  y += 3;
  
  doc.setFontSize(fontSize.small - 1);
  doc.text(new Date().toLocaleString('es-MX'), centerX, y, { align: 'center' });
  y += 5;
  
  // Pie de página - Aether Payment Manager
  doc.setFontSize(fontSize.small - 1);
  doc.setFont('helvetica', 'italic');
  doc.text('Aether Payment Manager', centerX, y, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  
  // Marca de agua si está cancelado o reembolsado
  if (recibo.estado === 'cancelado' || recibo.estado === 'reembolsado') {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(200, 0, 0);
    doc.text(recibo.estado.toUpperCase(), centerX, 50, { 
      align: 'center',
      angle: -30
    });
    doc.setTextColor(0, 0, 0);
  }
  
  // Imprimir o descargar
  if (print) {
    window.open(doc.output('bloburl'), '_blank');
  } else {
    doc.save(`Ticket_${recibo.numero_recibo || recibo.id}_${Date.now()}.pdf`);
  }
  
  return doc;
};

/**
 * Genera un comprobante de pago en formato térmico
 * @param {Object} pago - Datos del pago
 * @param {Object} recibo - Datos del recibo asociado
 * @param {Object} config - Configuración de la empresa
 * @param {number} paperWidth - Ancho del papel (80 o 58)
 * @param {boolean} print - Si es true, abre para imprimir
 */
export const generateThermalPaymentReceipt = (pago, recibo, config, paperWidth = 80, print = false) => {
  const size = PAPER_SIZES[paperWidth] || PAPER_SIZES[80];
  const margin = size.marginX;
  const contentWidth = size.width - (margin * 2);
  
  const doc = createThermalDoc(paperWidth, 120);
  let y = 5;
  
  const fontSize = {
    title: paperWidth === 80 ? 12 : 10,
    subtitle: paperWidth === 80 ? 10 : 8,
    normal: paperWidth === 80 ? 8 : 7,
    small: paperWidth === 80 ? 7 : 6
  };
  
  const centerX = size.width / 2;
  
  // === ENCABEZADO ===
  const empresa = config?.nombre_empresa || 'Mi Empresa';
  
  // Nombre de empresa con tamaño responsivo
  let empresaFontSize = fontSize.title;
  if (empresa.length > 25) {
    empresaFontSize = fontSize.subtitle;
  }
  if (empresa.length > 35) {
    empresaFontSize = fontSize.normal;
  }
  if (empresa.length > 45) {
    empresaFontSize = fontSize.small;
  }
  
  doc.setFontSize(empresaFontSize);
  doc.setFont('helvetica', 'bold');
  const empresaLines = doc.splitTextToSize(empresa, contentWidth);
  empresaLines.forEach(line => {
    doc.text(line, centerX, y, { align: 'center' });
    y += empresaFontSize <= fontSize.small ? 3 : 4;
  });
  y += 1;
  
  if (config?.direccion) {
    doc.setFontSize(fontSize.small);
    doc.setFont('helvetica', 'normal');
    const dirLines = doc.splitTextToSize(config.direccion, contentWidth);
    dirLines.forEach(line => {
      doc.text(line, centerX, y, { align: 'center' });
      y += 3;
    });
  }
  
  if (config?.telefono) {
    doc.text(`Tel: ${config.telefono}`, centerX, y, { align: 'center' });
    y += 3;
  }
  
  // Línea
  y += 2;
  doc.line(margin, y, size.width - margin, y);
  y += 4;
  
  // === TÍTULO ===
  doc.setFontSize(fontSize.subtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('COMPROBANTE DE PAGO', centerX, y, { align: 'center' });
  y += 5;
  
  // Línea punteada
  doc.setLineDashPattern([1, 1], 0);
  doc.line(margin, y, size.width - margin, y);
  doc.setLineDashPattern([], 0);
  y += 4;
  
  // === DATOS DEL PAGO ===
  doc.setFontSize(fontSize.normal);
  doc.setFont('helvetica', 'normal');
  
  doc.text(`Fecha: ${formatDate(pago.fecha_pago)}`, margin, y);
  y += 4;
  
  doc.text(`Recibo: ${recibo?.numero_recibo || pago.recibo_id || 'N/A'}`, margin, y);
  y += 4;
  
  doc.text(`Cliente: ${recibo?.cliente_nombre || pago.cliente_nombre || 'N/A'}`, margin, y);
  y += 4;
  
  if (pago.metodo_pago) {
    doc.text(`Método: ${pago.metodo_pago}`, margin, y);
    y += 4;
  }
  
  if (pago.referencia) {
    doc.text(`Ref: ${pago.referencia}`, margin, y);
    y += 4;
  }
  
  // === MONTO ===
  y += 2;
  doc.setLineWidth(1);
  doc.line(margin, y, size.width - margin, y);
  y += 5;
  
  doc.setFontSize(fontSize.subtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('MONTO PAGADO:', margin, y);
  y += 5;
  
  doc.setFontSize(fontSize.title);
  doc.text(formatCurrency(pago.monto), centerX, y, { align: 'center' });
  y += 5;
  
  // Saldo restante
  if (recibo && recibo.saldo_pendiente !== undefined) {
    doc.setFontSize(fontSize.normal);
    doc.setFont('helvetica', 'normal');
    doc.text(`Saldo restante: ${formatCurrency(recibo.saldo_pendiente)}`, centerX, y, { align: 'center' });
    y += 4;
  }
  
  // === PIE ===
  y += 3;
  doc.setLineDashPattern([1, 1], 0);
  doc.line(margin, y, size.width - margin, y);
  doc.setLineDashPattern([], 0);
  y += 4;
  
  doc.setFontSize(fontSize.small);
  doc.text('¡Gracias por su pago!', centerX, y, { align: 'center' });
  y += 4;
  doc.text(new Date().toLocaleString('es-MX'), centerX, y, { align: 'center' });
  y += 5;
  
  // Pie de página - Aether Payment Manager
  doc.setFont('helvetica', 'italic');
  doc.text('Aether Payment Manager', centerX, y, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  
  if (print) {
    window.open(doc.output('bloburl'), '_blank');
  } else {
    doc.save(`Pago_${pago.id || Date.now()}.pdf`);
  }
  
  return doc;
};

/**
 * Genera un reporte resumido en formato térmico
 * @param {Object} data - Datos del reporte
 * @param {string} title - Título del reporte
 * @param {Object} config - Configuración de la empresa
 * @param {number} paperWidth - Ancho del papel
 * @param {boolean} print - Si es true, abre para imprimir
 */
export const generateThermalReport = (data, title, config, paperWidth = 80, print = false) => {
  const size = PAPER_SIZES[paperWidth] || PAPER_SIZES[80];
  const margin = size.marginX;
  const contentWidth = size.width - (margin * 2);
  
  // Estimar altura basada en los datos
  const dataEntries = Object.entries(data);
  const estimatedHeight = 60 + (dataEntries.length * 6);
  
  const doc = createThermalDoc(paperWidth, estimatedHeight);
  let y = 5;
  
  const fontSize = {
    title: paperWidth === 80 ? 11 : 9,
    normal: paperWidth === 80 ? 8 : 7,
    small: paperWidth === 80 ? 7 : 6
  };
  
  const centerX = size.width / 2;
  
  // === ENCABEZADO ===
  const empresa = config?.nombre_empresa || 'Mi Empresa';
  
  // Nombre de empresa con tamaño responsivo
  let empresaFontSize = fontSize.title;
  if (empresa.length > 25) {
    empresaFontSize = fontSize.normal;
  }
  if (empresa.length > 35) {
    empresaFontSize = fontSize.small;
  }
  
  doc.setFontSize(empresaFontSize);
  doc.setFont('helvetica', 'bold');
  const empresaLines = doc.splitTextToSize(empresa, contentWidth);
  empresaLines.forEach(line => {
    doc.text(line, centerX, y, { align: 'center' });
    y += empresaFontSize <= fontSize.small ? 3 : 4;
  });
  y += 1;
  
  doc.line(margin, y, size.width - margin, y);
  y += 4;
  
  // === TÍTULO ===
  doc.text(title.toUpperCase(), centerX, y, { align: 'center' });
  y += 5;
  
  doc.setLineDashPattern([1, 1], 0);
  doc.line(margin, y, size.width - margin, y);
  doc.setLineDashPattern([], 0);
  y += 4;
  
  // === DATOS ===
  doc.setFontSize(fontSize.normal);
  doc.setFont('helvetica', 'normal');
  
  dataEntries.forEach(([key, value]) => {
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    doc.text(`${label}:`, margin, y);
    
    const valueStr = typeof value === 'number' 
      ? (key.includes('monto') || key.includes('total') || key.includes('saldo') 
          ? formatCurrency(value) 
          : value.toLocaleString())
      : String(value);
    
    doc.text(valueStr, size.width - margin, y, { align: 'right' });
    y += 4;
  });
  
  // === PIE ===
  y += 3;
  doc.line(margin, y, size.width - margin, y);
  y += 4;
  
  doc.setFontSize(fontSize.small);
  doc.text(new Date().toLocaleString('es-MX'), centerX, y, { align: 'center' });
  y += 5;
  
  // Pie de página - Aether Payment Manager
  doc.setFont('helvetica', 'italic');
  doc.text('Aether Payment Manager', centerX, y, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  
  if (print) {
    window.open(doc.output('bloburl'), '_blank');
  } else {
    doc.save(`Reporte_${Date.now()}.pdf`);
  }
  
  return doc;
};

/**
 * Genera un estado de cuenta con recibos desglosados en formato térmico
 * @param {Object} client - Datos del cliente
 * @param {Array} recibosDesglose - Recibos con items[] y pagos[] incluidos
 * @param {Object} config - Configuración de la empresa
 * @param {number} paperWidth - Ancho del papel (80 o 58)
 * @param {boolean} print - Si es true, abre para imprimir
 */
export const generateThermalEstadoCuenta = (client, recibosDesglose, config, paperWidth = 80, print = false) => {
  const size = PAPER_SIZES[paperWidth] || PAPER_SIZES[80];
  const margin = size.marginX;
  const contentWidth = size.width - (margin * 2);
  const rightEdge = size.width - margin;
  const centerX = size.width / 2;

  const fs = {
    title: paperWidth === 80 ? 11 : 9,
    subtitle: paperWidth === 80 ? 9 : 8,
    normal: paperWidth === 80 ? 8 : 7,
    small: paperWidth === 80 ? 7 : 6,
  };

  // Estimar altura: encabezado + resumen + por cada recibo sus items y pagos
  const totalItems = recibosDesglose.reduce((s, r) => s + (r.items?.length || 1), 0);
  const totalPagos = recibosDesglose.reduce((s, r) => s + (r.pagos?.length || 0), 0);
  const estimatedHeight = 90 + (recibosDesglose.length * 28) + (totalItems * 9) + (totalPagos * 7);

  const doc = createThermalDoc(paperWidth, estimatedHeight);
  let y = 4;

  const sep = (dashed = false) => {
    if (dashed) {
      doc.setLineDashPattern([0.5, 0.5], 0);
      doc.line(margin, y, rightEdge, y);
      doc.setLineDashPattern([], 0);
    } else {
      doc.setLineWidth(0.3);
      doc.line(margin, y, rightEdge, y);
    }
    y += 3;
  };

  // === ENCABEZADO EMPRESA ===
  const empresa = config?.nombre_empresa || 'Mi Empresa';
  let efSize = fs.title;
  if (empresa.length > 25) efSize = fs.subtitle;
  if (empresa.length > 35) efSize = fs.normal;

  doc.setFontSize(efSize);
  doc.setFont('helvetica', 'bold');
  doc.splitTextToSize(empresa, contentWidth).forEach(line => {
    doc.text(line, centerX, y, { align: 'center' });
    y += efSize <= fs.normal ? 3 : 4;
  });

  if (config?.direccion) {
    doc.setFontSize(fs.small);
    doc.setFont('helvetica', 'normal');
    doc.splitTextToSize(config.direccion, contentWidth).forEach(line => {
      doc.text(line, centerX, y, { align: 'center' });
      y += 3;
    });
  }
  if (config?.telefono) {
    doc.setFontSize(fs.small);
    doc.text(`Tel: ${config.telefono}`, centerX, y, { align: 'center' });
    y += 3;
  }

  y += 1;
  sep();

  // === TÍTULO ===
  doc.setFontSize(fs.subtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('ESTADO DE CUENTA', centerX, y, { align: 'center' });
  y += 4;
  sep(true);

  // === CLIENTE ===
  doc.setFontSize(fs.small);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENTE:', margin, y);
  y += 3;
  doc.setFont('helvetica', 'normal');
  doc.splitTextToSize(client?.nombre || 'N/A', contentWidth).forEach(line => {
    doc.text(line, margin, y);
    y += 3;
  });
  if (client?.telefono) { doc.text(`Tel: ${client.telefono}`, margin, y); y += 3; }
  if (client?.email)    { doc.text(`Email: ${client.email}`, margin, y); y += 3; }

  doc.setFontSize(fs.small);
  doc.setFont('helvetica', 'italic');
  doc.text(`Generado: ${new Date().toLocaleDateString('es-MX')}`, margin, y);
  doc.setFont('helvetica', 'normal');
  y += 4;
  sep(true);

  // === RESUMEN ===
  const totalMonto = recibosDesglose.reduce((s, r) => s + parseFloat(r.monto_total || 0), 0);
  const totalPendiente = recibosDesglose.reduce((s, r) => s + parseFloat(r.saldo_pendiente || 0), 0);
  const totalPagado = totalMonto - totalPendiente;

  doc.setFontSize(fs.normal);
  doc.setFont('helvetica', 'bold');
  doc.text('RESUMEN', margin, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.text(`Recibos: ${recibosDesglose.length}`, margin, y);
  y += 3;
  doc.text('Total facturado:', margin, y);
  doc.text(formatCurrency(totalMonto), rightEdge, y, { align: 'right' });
  y += 3;
  doc.text('Total pagado:', margin, y);
  doc.text(formatCurrency(totalPagado), rightEdge, y, { align: 'right' });
  y += 3;
  doc.setFont('helvetica', 'bold');
  doc.text('Saldo pendiente:', margin, y);
  doc.text(formatCurrency(totalPendiente), rightEdge, y, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  y += 5;
  sep();

  // === DETALLE POR RECIBO ===
  doc.setFontSize(fs.subtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('DETALLE DE RECIBOS', centerX, y, { align: 'center' });
  y += 5;

  for (const recibo of recibosDesglose) {
    sep(true);

    // Cabecera del recibo
    doc.setFontSize(fs.normal);
    doc.setFont('helvetica', 'bold');
    doc.text(`${recibo.numero_recibo || 'N/A'}`, margin, y);
    const estado = (recibo.estado || '').toUpperCase();
    doc.text(estado, rightEdge, y, { align: 'right' });
    y += 3;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fs.small);
    doc.text(`Emision: ${formatDate(recibo.fecha_emision)}   Vence: ${formatDate(recibo.fecha_vencimiento)}`, margin, y);
    y += 4;

    // Items
    if (recibo.items && recibo.items.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('DETALLE', margin, y);
      doc.text('IMPORTE', rightEdge, y, { align: 'right' });
      y += 1;
      doc.setLineWidth(0.1);
      doc.line(margin, y, rightEdge, y);
      y += 2;
      doc.setFont('helvetica', 'normal');

      for (const item of recibo.items) {
        const maxW = contentWidth - 22;
        const descLines = doc.splitTextToSize(item.descripcion || '', maxW);
        doc.text(descLines[0], margin, y);
        doc.text(formatCurrency(item.subtotal || (item.cantidad * item.precio_unitario)), rightEdge, y, { align: 'right' });
        y += 3;
        if (descLines[1]) { doc.text(descLines[1], margin, y); y += 3; }
        if ((item.cantidad || 1) > 1) {
          doc.text(`  ${item.cantidad} x ${formatCurrency(item.precio_unitario)}`, margin, y);
          y += 3;
        }
      }
    } else if (recibo.descripcion) {
      doc.setFont('helvetica', 'normal');
      doc.splitTextToSize(recibo.descripcion, contentWidth).forEach(line => {
        doc.text(line, margin, y); y += 3;
      });
    }

    // Totales del recibo
    y += 1;
    doc.setLineWidth(0.2);
    doc.line(margin, y, rightEdge, y);
    y += 2;

    const montoIva = parseFloat(recibo.monto_iva || 0);
    const montoDesc = parseFloat(recibo.monto_descuento || 0);
    const montoTotal = parseFloat(recibo.monto_total || 0);
    const saldo = parseFloat(recibo.saldo_pendiente || 0);
    const pagado = montoTotal - saldo;

    doc.setFontSize(fs.small);
    if (montoIva > 0) {
      doc.text(`IVA (${recibo.iva || 16}%):`, margin, y);
      doc.text(formatCurrency(montoIva), rightEdge, y, { align: 'right' });
      y += 3;
    }
    if (montoDesc > 0) {
      doc.text('Descuento:', margin, y);
      doc.text(`-${formatCurrency(montoDesc)}`, rightEdge, y, { align: 'right' });
      y += 3;
    }
    doc.setFont('helvetica', 'bold');
    doc.text('Total:', margin, y);
    doc.text(formatCurrency(montoTotal), rightEdge, y, { align: 'right' });
    y += 3;
    doc.setFont('helvetica', 'normal');
    if (pagado > 0) {
      doc.text('Pagado:', margin, y);
      doc.text(formatCurrency(pagado), rightEdge, y, { align: 'right' });
      y += 3;
    }
    doc.text('Saldo:', margin, y);
    doc.text(formatCurrency(saldo), rightEdge, y, { align: 'right' });
    y += 4;

    // Pagos del recibo
    if (recibo.pagos && recibo.pagos.length > 0) {
      doc.setFontSize(fs.small);
      doc.setFont('helvetica', 'bold');
      doc.text('Pagos:', margin, y);
      y += 3;
      doc.setFont('helvetica', 'normal');
      for (const pago of recibo.pagos) {
        doc.text(formatDate(pago.fecha_pago), margin, y);
        doc.text(formatCurrency(pago.monto), rightEdge, y, { align: 'right' });
        y += 3;
        if (pago.metodo_pago) {
          doc.text(`  ${pago.metodo_pago}`, margin, y);
          y += 3;
        }
      }
    }

    y += 2;
  }

  // === PIE ===
  sep(true);
  doc.setFontSize(fs.small);
  doc.text(new Date().toLocaleString('es-MX'), centerX, y, { align: 'center' });
  y += 4;
  doc.setFont('helvetica', 'italic');
  doc.text('Aether Payment Manager', centerX, y, { align: 'center' });
  doc.setFont('helvetica', 'normal');

  if (print) {
    window.open(doc.output('bloburl'), '_blank');
  } else {
    const nombre = (client?.nombre || 'Cliente').replace(/\s/g, '_');
    doc.save(`EstadoCuenta_${nombre}_${Date.now()}.pdf`);
  }

  return doc;
};

export default {
  createThermalDoc,
  generateThermalReceipt,
  generateThermalPaymentReceipt,
  generateThermalReport,
  generateThermalEstadoCuenta,
};
